import {
  Box,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import { formatRatio, formatYearMonth } from 'utils/rag';
import type {
  APILeadSourceEntry,
  APIOverviewTrendPoint,
} from 'types/api.types';
import { KPICard } from './OverallKPICards';
import {
  CHANNEL_COLORS_HEX,
  CHANNEL_LABELS,
} from '../constants/sourceChannels';

type LeadGroupKey =
  | 'applied'
  | 'organic'
  | 'ai'
  | 'direct'
  | 'aggregator'
  | 'other';

interface LeadGroup {
  leads: number;
  new: number;
  in_progress: number;
  won: number;
  lost: number;
  close_rate: number;
  ttc_hours: number | null;
}

export interface LeadDetail {
  new: number;
  in_progress: number;
  won: number;
  lost: number;
  ttc_hours: number | null;
  avg_leads_per_day: number;
  avg_leads_per_broker_per_day: number;
  lead_groups: Record<LeadGroupKey, LeadGroup>;
}

export interface LeadEntity {
  quotes: number;
  won: number;
  archived: number;
  closing_ratio: number;
  detail: LeadDetail;
}

const LEAD_GROUPS: { key: LeadGroupKey; label: string; color: string }[] = [
  { key: 'applied', label: 'Applied', color: '#6B48FF' },
  { key: 'organic', label: 'Organic', color: '#38A169' },
  { key: 'ai', label: 'AI', color: '#319795' },
  { key: 'direct', label: 'Direct', color: '#3182CE' },
  { key: 'aggregator', label: 'MyChoice', color: '#D69E2E' },
  { key: 'other', label: 'Other', color: '#718096' },
];

const count = (v: number) => v.toLocaleString();
export const timeToCall = (v: number | null) =>
  v == null ? '—' : `${v.toFixed(1)} h`;
const perDay = (v: number) => (v > 0 ? v.toFixed(1) : '—');

function Donut({
  title,
  labels,
  values,
  colors,
}: {
  title: string;
  labels: string[];
  values: number[];
  colors: string[];
}) {
  return (
    <Box>
      <Text fontWeight="700" fontSize="sm" color="gray.700" mb="8px">
        {title}
      </Text>
      {values.some((v) => v > 0) ? (
        <Chart
          type="donut"
          height={230}
          series={values}
          options={{
            labels,
            colors,
            legend: { position: 'bottom', fontSize: '11px' },
            dataLabels: { enabled: false },
          }}
        />
      ) : (
        <Text fontSize="xs" color="gray.400" py="40px" textAlign="center">
          No data
        </Text>
      )}
    </Box>
  );
}

function LeadGroupTable({ detail }: { detail: LeadDetail }) {
  const hdrBg = useColorModeValue('gray.50', 'navy.700');
  const border = useColorModeValue('gray.200', 'gray.600');
  return (
    <Box
      borderRadius="8px"
      overflow="hidden"
      border="1px solid"
      borderColor={border}
    >
      <Table size="xs" variant="simple">
        <Thead bg={hdrBg}>
          <Tr>
            {[
              'Lead Group',
              'Leads',
              'New',
              'In Progress',
              'Won',
              'Lost',
              'Closing %',
              'Time to Call',
            ].map((h, i) => (
              <Th
                key={h}
                fontSize="10px"
                py="7px"
                pl={i === 0 ? '12px' : undefined}
                textAlign={i === 0 ? 'left' : 'center'}
              >
                {h}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {LEAD_GROUPS.map(({ key, label }) => {
            const w = detail.lead_groups[key];
            return (
              <Tr key={key}>
                <Td py="8px" pl="12px" fontSize="xs" fontWeight="600">
                  {label}
                </Td>
                {[w.leads, w.new, w.in_progress, w.won, w.lost].map((v, i) => (
                  <Td key={i} fontSize="xs" textAlign="center">
                    {count(v)}
                  </Td>
                ))}
                <Td fontSize="xs" textAlign="center">
                  {w.leads > 0 ? formatRatio(w.close_rate) : '—'}
                </Td>
                <Td fontSize="xs" textAlign="center">
                  {timeToCall(w.ttc_hours)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function LeadTrendChart({ trends }: { trends: APIOverviewTrendPoint[] }) {
  const ordered = [...trends].reverse();
  return (
    <Chart
      type="line"
      height={260}
      series={[
        { name: 'Total Leads', data: ordered.map((t) => t.quotes) },
        {
          name: 'Closing %',
          data: ordered.map((t) => Math.round(t.closing_ratio * 100)),
        },
      ]}
      options={{
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#6B48FF', '#38A169'],
        stroke: { curve: 'smooth', width: 2 },
        markers: { size: 4 },
        xaxis: {
          categories: ordered.map((t) =>
            formatYearMonth(t.year_month, 'short'),
          ),
        },
        yaxis: [
          { title: { text: 'Leads' } },
          { opposite: true, min: 0, max: 100, title: { text: 'Closing %' } },
        ],
        legend: { position: 'top', horizontalAlign: 'right' },
      }}
    />
  );
}

export function LeadInsights({
  entity,
  sources,
  trends,
}: {
  entity: LeadEntity;
  sources: Record<string, APILeadSourceEntry>;
  trends: APIOverviewTrendPoint[];
}) {
  const bg = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const { detail } = entity;
  const sourceEntries = Object.entries(sources).filter(([, s]) => s.leads > 0);
  const section = {
    bg,
    border: '1px solid',
    borderColor: border,
    borderRadius: '12px',
    p: '16px',
    mb: '20px',
  };

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 4 }} gap="12px" mb="20px">
        <KPICard title="Total Leads" primary={count(entity.quotes)} />
        <KPICard title="Won Leads" primary={count(entity.won)} />
        <KPICard
          title="Closing Ratio"
          primary={formatRatio(entity.closing_ratio)}
        />
        <KPICard
          title="Archived Leads"
          primary={
            entity.quotes > 0
              ? formatRatio(entity.archived / entity.quotes)
              : '—'
          }
          subItems={[{ label: 'Archived', value: count(entity.archived) }]}
        />
        <KPICard
          title="Average Leads Per Day"
          primary={perDay(detail.avg_leads_per_day)}
          badge="30 day avg"
          isPlaceholder={!detail.avg_leads_per_day}
        />
        <KPICard
          title="Average Leads Per Broker Per Day"
          primary={perDay(detail.avg_leads_per_broker_per_day)}
          badge="30 day avg"
          isPlaceholder={!detail.avg_leads_per_broker_per_day}
        />
        <KPICard
          title="Time to Call"
          primary={timeToCall(detail.ttc_hours)}
          isPlaceholder={detail.ttc_hours == null}
        />
        {LEAD_GROUPS.map(({ key, label }) => (
          <KPICard
            key={key}
            title={`${label} Leads`}
            primary={count(detail.lead_groups[key].leads)}
          />
        ))}
      </SimpleGrid>

      <Box {...section}>
        <Text fontWeight="700" fontSize="md" color="gray.800" mb="8px">
          Rolling 12 Months - Leads
        </Text>
        {trends.length > 0 ? (
          <LeadTrendChart trends={trends} />
        ) : (
          <Text fontSize="xs" color="gray.400" py="20px">
            No trend data
          </Text>
        )}
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px" {...section}>
        <Donut
          title="Lead Status"
          labels={['New', 'In Progress', 'Won', 'Lost']}
          values={[detail.new, detail.in_progress, detail.won, detail.lost]}
          colors={['#3182CE', '#DD6B20', '#38A169', '#E53E3E']}
        />
        <Donut
          title="Lead Group"
          labels={LEAD_GROUPS.map((w) => w.label)}
          values={LEAD_GROUPS.map((w) => detail.lead_groups[w.key].leads)}
          colors={LEAD_GROUPS.map((w) => w.color)}
        />
        <Donut
          title="Lead Source"
          labels={sourceEntries.map(([slug]) => CHANNEL_LABELS[slug] ?? slug)}
          values={sourceEntries.map(([, s]) => s.leads)}
          colors={sourceEntries.map(
            ([slug]) => CHANNEL_COLORS_HEX[slug] ?? '#718096',
          )}
        />
      </SimpleGrid>

      <Box mb="20px">
        <Text fontWeight="700" fontSize="sm" color="gray.700" mb="12px">
          Lead Groups
        </Text>
        <LeadGroupTable detail={detail} />
      </Box>
    </Box>
  );
}
